import { Color3, Constants, MeshBuilder, TransformNode, type Mesh, type Scene, type ShaderMaterial, type Vector3 } from '@babylonjs/core';
import { registerGroundLineShaders } from './ground-line-shaders';
import { GROUND_WARNING_COLOR_HEX, GROUND_WARNING_FLASH_COLOR_HEX } from './ground-warning-system';
import type { VfxWarmup } from './vfx-warmup';
import { clamp01 } from './easing';
import { createVfxShaderMaterial } from './vfx-shader-material';
import { reconcileKeyedEffects } from './keyed-effects';

/**
 * One line on the floor this frame: where a tethered throw will carry
 * whoever it catches, or the lane an enemy will charge down.
 */
export interface GroundLineState {
    readonly id: string;
    /** Where it starts and ends, on the floor, in scene space. */
    readonly from: Vector3;
    readonly to: Vector3;
    /** The share of it already behind what moves along it (0 to 1): faded away. */
    readonly consumed: number;
    /** A lane's width, edge to edge, in meters (a charge's): drawn with a rail along either edge and a subtle fill between. None by default. */
    readonly widthMeters?: number;
    /** The share of it filled (0 to 1): dimmer past it. A charge lane fills through its windup; full by default. */
    readonly filled?: number;
    /** The lock flash as what it warns of commits (a charge setting off): 1 fading to 0. */
    readonly lockFlash?: number;
}

/** The line's width, its spear tip's length and half-width, and how far its edges soften, in meters. */
const LINE_WIDTH_METERS = 0.035;
const TIP_LENGTH_METERS = 0.32;
const TIP_HALF_WIDTH_METERS = 0.09;
const FEATHER_METERS = 0.01;
/** The chevrons marching along the line: how far apart, how wide either side of it, and how thick (meters). */
const CHEVRON_SPACING_METERS = 0.4;
const CHEVRON_HALF_WIDTH_METERS = 0.08;
const CHEVRON_THICKNESS_METERS = 0.021;
/** How fast the chevrons march, in meters a second. */
const CHEVRON_SPEED_METERS_PER_SECOND = 1.5;
const LINE_COLOR = Color3.FromHexString(GROUND_WARNING_COLOR_HEX);
const FLASH_COLOR = Color3.FromHexString(GROUND_WARNING_FLASH_COLOR_HEX);
/** Just above the floor, under ground warnings and bodies. */
const FLOOR_OFFSET = 0.03;

interface DrawnLine {
    readonly root: TransformNode;
    readonly mesh: Mesh;
    readonly material: ShaderMaterial;
    /** What its mesh was built for: a line whose length or width changes is built again. */
    readonly length: number;
    readonly laneHalfWidth: number;
}

/**
 * Draws lines on the floor that say where something will go (combat-vfx.md,
 * "Projectiles" and "Charge"): the thin line a tethered throw lays from
 * where it lands to where it carries whoever it catches, and a charge's
 * lane. Each is a thin line ending in a spear tip, with chevrons marching
 * along it toward the tip; a lane adds a thin rail along either edge and a
 * subtle fill between, to show what it covers. Matte, in the ground warnings' red.
 * Takes exactly the lines shown this frame; `update` marches the chevrons.
 */
export class GroundLineSystem {
    private readonly drawn = new Map<string, DrawnLine>();
    private time = 0;

    constructor(private readonly scene: Scene) {
        registerGroundLineShaders();
    }

    warm(warmup: VfxWarmup): void {
        warmup.material(this.createMaterial('ground-line-warmup'));
    }

    setLines(states: readonly GroundLineState[]): void {
        const lengthOf = (state: GroundLineState): number => Math.max(0.01, Math.hypot(state.to.x - state.from.x, state.to.z - state.from.z));
        const laneHalfWidthOf = (state: GroundLineState): number => (state.widthMeters !== undefined ? state.widthMeters / 2 : 0);
        reconcileKeyedEffects(this.drawn, states, {
            create: (state) => this.create(state.id, lengthOf(state), laneHalfWidthOf(state)),
            // Its quad is built to its length and lane width.
            isStale: (line, state) => Math.abs(line.length - lengthOf(state)) > 1e-3 || line.laneHalfWidth !== laneHalfWidthOf(state),
            apply: (line, state) => {
                line.root.position.set((state.from.x + state.to.x) / 2, state.from.y + FLOOR_OFFSET, (state.from.z + state.to.z) / 2);
                // A ground mesh runs along its local x: turned about the vertical to point from start to end.
                line.root.rotation.y = Math.atan2(-(state.to.z - state.from.z), state.to.x - state.from.x);
                line.material.setFloat('consumed', clamp01(state.consumed));
                line.material.setFloat('filled', clamp01(state.filled ?? 1));
                line.material.setFloat('flash', clamp01(state.lockFlash ?? 0));
                line.material.setFloat('opacity', 1);
            },
            unseen: () => 'remove',
            discard: (line) => this.disposeLine(line),
        });
    }

    update(deltaSeconds: number): void {
        this.time += deltaSeconds;
        this.drawn.forEach((line) => line.material.setFloat('time', this.time));
    }

    dispose(): void {
        this.drawn.forEach((line) => this.disposeLine(line));
        this.drawn.clear();
    }

    private create(id: string, length: number, laneHalfWidth: number): DrawnLine {
        const name = `ground-line-${id}`;
        const root = new TransformNode(name, this.scene);
        const quadWidth = (Math.max(TIP_HALF_WIDTH_METERS, laneHalfWidth + LINE_WIDTH_METERS) * 2) + (FEATHER_METERS * 4);
        const mesh = MeshBuilder.CreateGround(`${name}-mesh`, { width: length, height: quadWidth }, this.scene);
        mesh.parent = root;
        mesh.isPickable = false;
        const material = this.createMaterial(name);
        material.setFloat('lengthMeters', length);
        material.setFloat('quadWidth', quadWidth);
        material.setFloat('laneHalfWidth', laneHalfWidth);
        mesh.material = material;
        return { root, mesh, material, length, laneHalfWidth };
    }

    private createMaterial(name: string): ShaderMaterial {
        const material = createVfxShaderMaterial(this.scene, name, 'groundLine', {
            uniforms: [
                'worldViewProjection', 'lengthMeters', 'quadWidth', 'lineWidth', 'tipLength', 'tipHalfWidth',
                'chevronSpacing', 'chevronHalfWidth', 'chevronSpeed', 'chevronThickness', 'laneHalfWidth', 'time',
                'consumed', 'filled', 'flash', 'opacity', 'feather', 'lineColor', 'flashColor',
            ],
            alphaMode: Constants.ALPHA_COMBINE,
        });
        material.setFloat('lineWidth', LINE_WIDTH_METERS);
        material.setFloat('tipLength', TIP_LENGTH_METERS);
        material.setFloat('tipHalfWidth', TIP_HALF_WIDTH_METERS);
        material.setFloat('feather', FEATHER_METERS);
        material.setFloat('chevronSpacing', CHEVRON_SPACING_METERS);
        material.setFloat('chevronHalfWidth', CHEVRON_HALF_WIDTH_METERS);
        material.setFloat('chevronThickness', CHEVRON_THICKNESS_METERS);
        material.setFloat('chevronSpeed', CHEVRON_SPEED_METERS_PER_SECOND);
        material.setFloat('laneHalfWidth', 0);
        material.setFloat('time', this.time);
        material.setFloat('lengthMeters', 1);
        material.setFloat('quadWidth', 0.2);
        material.setFloat('consumed', 0);
        material.setFloat('filled', 1);
        material.setFloat('flash', 0);
        material.setFloat('opacity', 1);
        material.setColor3('lineColor', LINE_COLOR);
        material.setColor3('flashColor', FLASH_COLOR);
        return material;
    }

    private disposeLine(line: DrawnLine): void {
        line.material.dispose();
        line.mesh.dispose();
        line.root.dispose();
    }
}

